import * as React from 'react';
import { useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { ReloadOutlined } from '@ant-design/icons';
import { RootState } from '../../reducers/reducers';
import { selectAuthCredentials, selectUserAppDataByAppIdMap } from '../../selectors/selectors';
import { getPublicApiRequestLogs, PublicAPIRequestLog } from '../../api';
import { DatetimeFormat } from '../../timeHelpers';
import { globalMessage } from '../../utils/globalMessage';
import styles from '../styles/settingsStyles';

const { Text } = Typography;

const SOURCE_LABELS: Record<string, string> = {
    rest: 'REST',
    mcp: 'MCP',
    'mcp-chatbot': 'MCP (chatbot)',
};

const BOTTOM_PADDING_FOR_HELP_BUTTON = 96;

const REJECTED_DATA_POINTS_MAX_HEIGHT = 240;

const detailsElementId = (log: PublicAPIRequestLog) => `api-request-log-${log.id}`;

const noWrapCell = () => ({ style: { whiteSpace: 'nowrap' as const } });

const isSuccessStatus = (statusCode: number) => statusCode >= 200 && statusCode < 400;

const formatBody = (body: string) => {
    try {
        return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
        return body;
    }
};

interface PublicApiRequestLogProps {
    orgId: string;
}

const PublicApiRequestLog: React.FC<PublicApiRequestLogProps> = ({ orgId }) => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, orgId));

    const [logs, setLogs] = React.useState<PublicAPIRequestLog[]>([]);
    const [nextCursor, setNextCursor] = React.useState<string | null>(null);
    const [pathFilter, setPathFilter] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);

    const fetchLogs = React.useCallback(
        (filter: string, cursor: string | null) => {
            setIsLoading(true);
            getPublicApiRequestLogs(orgId, filter, cursor, auth)
                .then((response) => {
                    const fetched = response?.logs ?? [];
                    setLogs((previous) => (cursor ? [...previous, ...fetched] : fetched));
                    setNextCursor(
                        response?.paginationInfo?.hasMore ? (response.paginationInfo.nextCursor ?? null) : null,
                    );
                })
                .catch(() => globalMessage.error('Unable to load API requests'))
                .finally(() => setIsLoading(false));
        },
        [orgId, auth],
    );

    React.useEffect(() => fetchLogs('', null), [fetchLogs]);

    const onSearch = (value: string) => {
        const filter = value.trim();
        setPathFilter(filter);
        fetchLogs(filter, null);
    };

    const renderBody = (title: string, body?: string, truncated?: boolean) => {
        if (!body) {
            return null;
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
                <Text strong>
                    {title}
                    {truncated && <Text type="secondary"> (truncated)</Text>}
                </Text>
                <Typography.Paragraph style={{ marginBottom: 0, maxHeight: 320, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: 12 }}>{formatBody(body)}</pre>
                </Typography.Paragraph>
            </div>
        );
    };

    const scrollToDetails = (expanded: boolean, log: PublicAPIRequestLog) => {
        if (!expanded) {
            return;
        }
        requestAnimationFrame(() =>
            document.getElementById(detailsElementId(log))?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
        );
    };

    const renderDetails = (log: PublicAPIRequestLog) => (
        <div
            id={detailsElementId(log)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                scrollMarginBottom: BOTTOM_PADDING_FOR_HELP_BUTTON,
            }}
        >
            <Text>
                <Text type="secondary">API key</Text> {log.apiKeyName}
            </Text>
            {log.warnings?.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Text strong>Rejected data points</Text>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            maxHeight: REJECTED_DATA_POINTS_MAX_HEIGHT,
                            overflow: 'auto',
                        }}
                    >
                        {log.warnings.map((warning, index) => (
                            <Text key={index} type="warning">
                                {warning}
                            </Text>
                        ))}
                    </div>
                </div>
            )}
            <div style={{ display: 'flex', gap: 16 }}>
                {renderBody('Request body', log.requestBody, log.requestBodyTruncated)}
                {renderBody('Response body', log.responseBody, log.responseBodyTruncated)}
            </div>
        </div>
    );

    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: BOTTOM_PADDING_FOR_HELP_BUTTON }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={styles.appSettingsSectionTitleStyle}>API requests</Text>
                <Space size={8}>
                    <Input.Search
                        allowClear
                        placeholder="Filter by path, e.g. /events"
                        onSearch={onSearch}
                        style={{ width: 280 }}
                    />
                    <Button icon={<ReloadOutlined />} onClick={() => fetchLogs(pathFilter, null)} />
                </Space>
            </div>
            <Text type="secondary">Requests received by the Runway API in the last 30 days</Text>
            <Table
                rowKey="id"
                dataSource={logs}
                loading={isLoading}
                pagination={false}
                scroll={{ x: 'max-content' }}
                expandable={{
                    expandedRowRender: renderDetails,
                    onExpand: scrollToDetails,
                }}
                columns={[
                    {
                        title: 'Time',
                        dataIndex: 'createdAt',
                        width: 150,
                        onCell: noWrapCell,
                        render: (date: string) => dayjs(date).format(DatetimeFormat.ShortMonthDateTime2),
                    },
                    {
                        title: 'Status',
                        dataIndex: 'statusCode',
                        width: 90,
                        onCell: noWrapCell,
                        render: (statusCode: number, log: PublicAPIRequestLog) => (
                            <Space size={4}>
                                <Tag color={isSuccessStatus(statusCode) ? 'success' : 'error'}>{statusCode}</Tag>
                                {isSuccessStatus(statusCode) && log.warnings?.length > 0 && (
                                    <Tag color="warning">Partial</Tag>
                                )}
                            </Space>
                        ),
                    },
                    {
                        title: 'Request',
                        key: 'request',
                        width: 380,
                        ellipsis: { showTitle: false },
                        render: (_: unknown, log: PublicAPIRequestLog) => (
                            <Tooltip placement="topLeft" title={`${log.method} ${log.path}`}>
                                <span>
                                    <Text strong>{log.method}</Text> {log.path}
                                </span>
                            </Tooltip>
                        ),
                    },
                    {
                        title: 'App',
                        dataIndex: 'appId',
                        width: 200,
                        ellipsis: { showTitle: false },
                        render: (appId?: string) => {
                            if (!appId) {
                                return <Text type="secondary">--</Text>;
                            }
                            const appName = appDataByAppIdMap[appId]?.appName ?? appId;
                            return (
                                <Tooltip placement="topLeft" title={appName}>
                                    {appName}
                                </Tooltip>
                            );
                        },
                    },
                    {
                        title: 'Source',
                        dataIndex: 'source',
                        width: 130,
                        onCell: noWrapCell,
                        render: (source: string) => SOURCE_LABELS[source] ?? source,
                    },
                    {
                        title: 'Duration',
                        dataIndex: 'durationMs',
                        width: 100,
                        onCell: noWrapCell,
                        render: (durationMs: number) => `${durationMs} ms`,
                    },
                ]}
            />
            {nextCursor && (
                <Button
                    style={{ alignSelf: 'center' }}
                    loading={isLoading}
                    onClick={() => fetchLogs(pathFilter, nextCursor)}
                >
                    Load more
                </Button>
            )}
        </div>
    );
};

export default PublicApiRequestLog;
